import { UserName } from "../components/UserName.tsx";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  AckResult,
  DuelRules,
  FriendsChanged,
  PublicUser,
  TeamLobbyState,
} from "@mk/shared";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { FriendHistorySheet } from "../components/FriendHistorySheet.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Toast } from "../components/Toast.tsx";
import {
  challengeCooldownLeft,
  hasActiveCooldown,
  startChallengeCancelCooldown,
  startChallengeCooldown,
} from "../features/duel/challengeCooldown.ts";
import {
  duelRulesSummary,
  standardDuelRules,
  victoryRuleLabel,
} from "../features/duel/rules.ts";
import {
  loadFriendlyDuelRules,
  saveFriendlyDuelRules,
} from "../features/duel/friendlyRulesStorage.ts";
import { ApiError, api, post } from "../lib/api.ts";
import { errorMessage, errors, messages } from "../i18n/index.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { useSession } from "../lib/session.ts";
import { getSocket } from "../lib/socket.ts";
import { gameEdition } from "../lib/edition.ts";
import { EMPTY_FRIENDS_DATA, type FriendEntry, type FriendsData } from "../features/friends/types.ts";
import { joinTeamSpectating, useTeamStore } from "../features/team/teamStore.ts";

interface FriendsScreenProps {
  onBack: () => void;
  onDuel: () => void;
  onTeam?: () => void;
  onRace?: () => void;
  /** Arkadaşın maçını izlemeye geç. */
  onSpectate?: (matchId: string) => void;
  /** Onay penceresi açıkken geri tuşu önce onu kapatsın. */
  registerBackHandler?: RegisterBackHandler;
}

const CHANGE_NOTICE: Record<FriendsChanged["kind"], string> = {
  "request-created": messages.friendsScreen.changes.created,
  "request-accepted": messages.friendsScreen.changes.accepted,
  "request-declined": messages.friendsScreen.changes.declined,
  "friend-removed": messages.friendsScreen.changes.removed,
  "profile-updated": messages.friendsScreen.changes.profileUpdated,
};

export function FriendsScreen({
  onBack,
  onDuel,
  onTeam,
  onRace,
  onSpectate,
  registerBackHandler,
}: FriendsScreenProps) {
  const meId = useSession((state) => state.user?.id);
  const [data, setData] = useState<FriendsData>(EMPTY_FRIENDS_DATA);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PublicUser[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [watchingTeamMatchId, setWatchingTeamMatchId] = useState<string | null>(null);
  const [challengeSending, setChallengeSending] = useState(false);
  const [challengeCancellingId, setChallengeCancellingId] = useState<string | null>(null);
  const [configuring, setConfiguring] = useState<FriendEntry | null>(null);
  const [challengeRules, setChallengeRules] = useState<DuelRules>(standardDuelRules);
  const [challengeMode, setChallengeMode] = useState<"classic" | "race">("classic");
  const [historyFriend, setHistoryFriend] = useState<FriendEntry | null>(null);
  const [expandedFriendId, setExpandedFriendId] = useState<string | null>(null);
  /** Çıkarılması onaylanacak arkadaş; pencere kapalıysa null. */
  const [removing, setRemoving] = useState<FriendEntry | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const loadVersion = useRef(0);
  const refreshTimer = useRef<number | null>(null);
  const toastTimer = useRef<number | null>(null);

  const notify = useCallback((message: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, 2200);
  }, []);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    try {
      const next = await api<FriendsData>("/api/friends");
      if (version === loadVersion.current) {
        for (const challenge of next.sentChallenges) {
          startChallengeCooldown(challenge.to.id, challenge.expiresAt);
        }
        setNow(Date.now());
        setData(next);
      }
    } catch {
      if (version === loadVersion.current) notify(messages.friendsScreen.listFailed);
    }
  }, [notify]);

  const scheduleLoad = useCallback(() => {
    if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => {
      refreshTimer.current = null;
      void load();
    }, 60);
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  // Seçimler hesaba özel cihazda kalır; pencereyi kapatmak ayarları unutturmaz.
  useEffect(() => {
    if (configuring) saveFriendlyDuelRules(meId, challengeRules);
  }, [challengeRules, configuring, meId]);

  // Açık onay/ayar penceresi geri tuşunu önce kendisi tüketir.
  useEffect(() => {
    if ((!removing && !configuring) || !registerBackHandler) return;
    return registerBackHandler(() => {
      if (!busy && !challengeSending) {
        setRemoving(null);
        setConfiguring(null);
      }
      return "block";
    });
  }, [busy, challengeSending, configuring, registerBackHandler, removing]);

  useEffect(
    () => () => {
      if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  // Arkadaşlık, meydan okuma ve bağlantı değişiklikleri ekrandan çıkmadan yansısın.
  useEffect(() => {
    const socket = getSocket();
    const onChanged = (change: FriendsChanged) => {
      scheduleLoad();
      if (change.actorId !== meId) {
        notify(CHANGE_NOTICE[change.kind] ?? messages.friendsScreen.changes.updated);
      }
    };
    const onChallenge = () => scheduleLoad();
    const onChallengeCancelled = (payload: {
      challengeId: string;
      friendId?: string;
      cooldownEndsAt?: number;
    }) => {
      if (payload.friendId && payload.cooldownEndsAt) {
        startChallengeCancelCooldown(payload.friendId, payload.cooldownEndsAt);
        setNow(Date.now());
      }
      scheduleLoad();
    };
    const onPresence = ({ userId, online }: { userId: string; online: boolean }) => {
      setData((current) => ({
        ...current,
        friends: current.friends.map((friend) =>
          friend.user.id === userId ? { ...friend, online } : friend,
        ),
      }));
    };

    /* Arkadaş maça girdi ya da maçı bitti: "İzle" düğmesi buna göre çıkıp kayboluyor. */
    const onMatch = ({ userId, matchId }: { userId: string; matchId: string | null }) => {
      setData((current) => ({
        ...current,
        friends: current.friends.map((friend) =>
          friend.user.id === userId ? { ...friend, matchId } : friend,
        ),
      }));
    };

    socket.on("connect", scheduleLoad);
    socket.on("friends:changed", onChanged);
    socket.on("friend:presence", onPresence);
    socket.on("friend:match", onMatch);
    socket.on("friend:team-match", scheduleLoad);
    socket.on("challenge:incoming", onChallenge);
    socket.on("challenge:cancelled", onChallengeCancelled);
    socket.on("team:invite:incoming", onChallenge);
    socket.on("team:invite:cancelled", onChallenge);
    return () => {
      socket.off("connect", scheduleLoad);
      socket.off("friends:changed", onChanged);
      socket.off("friend:presence", onPresence);
      socket.off("friend:match", onMatch);
      socket.off("friend:team-match", scheduleLoad);
      socket.off("challenge:incoming", onChallenge);
      socket.off("challenge:cancelled", onChallengeCancelled);
      socket.off("team:invite:incoming", onChallenge);
      socket.off("team:invite:cancelled", onChallenge);
    };
  }, [meId, notify, scheduleLoad]);

  async function search(event: FormEvent) {
    event.preventDefault();
    const q = query.trim();
    if (q.length < 3) {
      notify(messages.friendsScreen.minSearch);
      return;
    }
    try {
      const res = await api<{ results: PublicUser[] }>(`/api/friends/search?q=${encodeURIComponent(q)}`);
      setResults(res.results);
    } catch {
      notify(messages.friendsScreen.searchFailed);
    }
  }

  async function addFriend(userId: string) {
    setBusy(true);
    try {
      const res = await post<{ status: string }>("/api/friends/request", { userId });
      notify(res.status === "accepted" ? messages.friendsScreen.nowFriends : messages.friendsScreen.requestSent);
      setResults(null);
      setQuery("");
      await load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : messages.friendsScreen.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Arkadaşlığı bitirir.
   *
   * Kayıt tek satır ve iki yönlü: karşı taraf da listeden düşüyor ve sunucu
   * ona `friends:changed` yolluyor. Düello karnesi maçlardan hesaplandığı için
   * silinmiyor — yeniden eklerseniz aynı skor geri geliyor.
   */
  async function removeFriend(entry: FriendEntry) {
    setBusy(true);
    try {
      await api(`/api/friends/${entry.friendshipId}`, { method: "DELETE" });
      setRemoving(null);
      notify(messages.friendsScreen.removed(entry.user.username));
      await load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : messages.friendsScreen.removeFailed);
    } finally {
      setBusy(false);
    }
  }

  async function respond(friendshipId: string, accept: boolean) {
    setBusy(true);
    try {
      await post("/api/friends/respond", { friendshipId, accept });
      await load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : messages.friendsScreen.actionFailed);
    } finally {
      setBusy(false);
    }
  }

  /*
   * Çevrimiçi arkadaşlar başta.
   *
   * Sıralama istemcide: bağlantı durumu maça girip çıktıkça sokete geliyor,
   * sunucudan gelen listeyi bir kez sıralamak yetmezdi. Aynı gruptakiler
   * Türkçe alfabeye göre — "Işık" ile "İpek" doğru yerde dursun.
   */
  const friends = useMemo(
    () =>
      [...data.friends].sort(
        (a, b) =>
          Number(b.online) - Number(a.online) ||
          gameEdition.compareWords(a.user.username, b.user.username),
      ),
    [data.friends],
  );

  /*
   * Geri sayım yalnızca bekleyen davet varken tıklıyor; son davet düşünce
   * `now` güncellenir, `waiting` false olur ve sayaç kendini kapatır.
   */
  const waiting = hasActiveCooldown(now) || data.challenges.length > 0 ||
    data.teamInvites.length > 0 || data.sentTeamInvites.length > 0;
  useEffect(() => {
    if (!waiting) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [waiting]);

  function openChallengeSettings(friend: FriendEntry) {
    setExpandedFriendId(null);
    setHistoryFriend(null);
    setChallengeRules(loadFriendlyDuelRules(meId));
    setChallengeMode("classic");
    setConfiguring(friend);
    setNow(Date.now());
  }

  function openFriendHistory(friend: FriendEntry) {
    setExpandedFriendId(null);
    setConfiguring(null);
    setHistoryFriend(friend);
  }

  function challenge() {
    if (!configuring || challengeSending) return;
    if (challengeCooldownLeft(configuring.user.id, Date.now()) > 0) return;

    setChallengeSending(true);
    getSocket().emit(
      "challenge:send",
      { friendId: configuring.user.id, rules: challengeMode === "classic" ? challengeRules : undefined, mode: challengeMode },
      (res: AckResult<{ challengeId: string; expiresAt: number }>) => {
      setChallengeSending(false);
      if (res?.ok) {
        startChallengeCooldown(configuring.user.id, res.data.expiresAt);
        setNow(Date.now());
        setConfiguring(null);
        notify(messages.friendsScreen.challengeSent);
        void load();
        return;
      }
      notify(res ? errorMessage(res) : errors.challenge_send_failed());
      },
    );
  }

  function answerChallenge(challengeId: string, accept: boolean, mode: "classic" | "race" = "classic") {
    getSocket().emit("challenge:respond", { challengeId, accept }, (res: AckResult<unknown>) => {
      if (!res?.ok) {
        notify(res ? errorMessage(res) : errors.challenge_response_failed());
        return;
      }
      void load();
      if (accept) (mode === "race" ? onRace : onDuel)?.();
    });
  }

  function cancelSentChallenge(challengeId: string, friendId: string) {
    if (challengeCancellingId !== null) return;
    setChallengeCancellingId(challengeId);
    getSocket().emit(
      "challenge:cancel",
      { challengeId },
      (res: AckResult<{ cooldownEndsAt: number }>) => {
        setChallengeCancellingId(null);
        if (!res?.ok) {
          notify(res ? errorMessage(res) : errors.challenge_cancel_failed());
          return;
        }
        startChallengeCancelCooldown(friendId, res.data.cooldownEndsAt);
        setNow(Date.now());
        setData((current) => ({
          ...current,
          sentChallenges: current.sentChallenges.filter((item) => item.challengeId !== challengeId),
        }));
        notify(messages.friendsScreen.challengeCancelled(
          Math.max(1, Math.ceil((res.data.cooldownEndsAt - Date.now()) / 1000)),
        ));
        void load();
      },
    );
  }

  function answerTeamInvite(inviteId: string, accept: boolean) {
    getSocket().emit(
      "team:invite:respond",
      { inviteId, accept },
      (res: AckResult<{ lobby: TeamLobbyState | null }>) => {
        if (!res?.ok) {
          notify(res ? errorMessage(res) : errors.challenge_response_failed());
          return;
        }
        void load();
        if (accept && res.data.lobby) {
          useTeamStore.getState().setLobby(res.data.lobby);
          onTeam?.();
        }
      },
    );
  }

  async function watchTeamMatch(friend: FriendEntry) {
    const matchId = friend.teamMatchId;
    if (!matchId || !friend.teamMatchWatchable || watchingTeamMatchId !== null) return;
    setWatchingTeamMatchId(matchId);
    const response = await joinTeamSpectating(matchId);
    setWatchingTeamMatchId((current) => current === matchId ? null : current);
    if (!response.ok) {
      notify(errorMessage(response));
      scheduleLoad();
      return;
    }
    onTeam?.();
  }

  function primaryFriendAction(friend: FriendEntry, cooldownLeft: number, mobile = false) {
    const mobileClass = mobile ? " friend-card__mobile-primary" : "";
    if (friend.matchId && onSpectate) {
      return (
        <button
          type="button"
          className={`chip-btn chip-btn--watch${mobileClass}`}
          onClick={() => onSpectate(friend.matchId!)}
        >
          <span className="live-dot" aria-hidden="true" />
          {messages.friendsScreen.watch}
        </button>
      );
    }
    if (friend.teamMatchId && friend.teamMatchWatchable && onTeam) {
      return (
        <button
          type="button"
          className={`chip-btn chip-btn--watch${mobileClass}`}
          disabled={watchingTeamMatchId !== null}
          onClick={() => void watchTeamMatch(friend)}
        >
          <span className="live-dot" aria-hidden="true" />
          {messages.friendsScreen.watchTeam}
        </button>
      );
    }
    if (friend.teamMatchId) {
      return (
        <button
          type="button"
          className={`chip-btn chip-btn--team-unavailable${mobileClass}`}
          disabled
          title={messages.friendsScreen.teamSpectatorsDisabled}
        >
          {messages.friendsScreen.teamSpectatorsDisabled}
        </button>
      );
    }
    return (
      <button
        type="button"
        className={`chip-btn chip-btn--duel${mobileClass}`}
        onClick={() => openChallengeSettings(friend)}
        disabled={!friend.online}
        title={
          cooldownLeft > 0
            ? messages.friendsScreen.prepareSettings
            : friend.online
              ? undefined
              : messages.friendsScreen.offline
        }
      >
        {cooldownLeft > 0
          ? messages.friendsScreen.settingsWithSeconds(cooldownLeft)
          : friend.online
            ? messages.friendsScreen.challenge
            : messages.friendsScreen.offlineShort}
      </button>
    );
  }

  return (
    <div className="app">
      <ScreenHeader title={messages.friendsScreen.title} onBack={onBack} />

      <main className="relative overflow-y-auto px-4 py-5">
        <Toast message={toast} />
        <div className="mx-auto flex w-full max-w-md flex-col gap-6">
          {(data.challenges.length > 0 || data.teamInvites.length > 0) && (
            <section>
              <h3 className="section-title">{messages.friendsScreen.challenged}</h3>
              <ul className="flex flex-col gap-2">
                {data.challenges.map((item) => (
                  <li key={item.challengeId} className="row-card row-card--duel">
                    <span className="min-w-0 flex-1">
                      <span className="row-card__name">{<UserName user={item.from} />} · {item.mode === "race" ? messages.race.title : messages.invites.duel}</span>
                      <span className="mt-1 block text-[0.68rem] leading-relaxed text-muted">
                        {duelRulesSummary(item.rules)} · {messages.invites.remaining(Math.max(0, Math.ceil((item.expiresAt - now) / 1000)))}
                      </span>
                    </span>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        className="chip-btn"
                          onClick={() => answerChallenge(item.challengeId, false, item.mode)}
                      >
                        {messages.rematch.decline}
                      </button>
                      <button
                        type="button"
                        className="chip-btn chip-btn--primary"
                          onClick={() => answerChallenge(item.challengeId, true, item.mode)}
                      >
                        {messages.rematch.accept}
                      </button>
                    </div>
                  </li>
                ))}
                {data.teamInvites.map((item) => (
                  <li key={item.inviteId} className="row-card row-card--duel">
                    <span className="min-w-0 flex-1">
                      <span className="row-card__name">{<UserName user={item.from} />} · {messages.invites.team}</span>
                      <span className="mt-1 block text-[0.68rem] leading-relaxed text-muted">
                        {messages.invites.teamSummary(item.code, item.settings.durationMinutes, item.playerCount)} · {messages.invites.remaining(Math.max(0, Math.ceil((item.expiresAt - now) / 1000)))}
                      </span>
                    </span>
                    <div className="flex gap-1.5">
                      <button type="button" className="chip-btn" onClick={() => answerTeamInvite(item.inviteId, false)}>
                        {messages.rematch.decline}
                      </button>
                      <button type="button" className="chip-btn chip-btn--primary" onClick={() => answerTeamInvite(item.inviteId, true)}>
                        {messages.rematch.accept}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(data.sentChallenges.length > 0 || data.sentTeamInvites.length > 0) && (
            <section>
              <h3 className="section-title">{messages.friendsScreen.pendingChallenges}</h3>
              <ul className="flex flex-col gap-2">
                {data.sentChallenges.map((item) => (
                  <li key={item.challengeId} className="row-card">
                    <span className="min-w-0 flex-1">
                      <span className="row-card__name">{<UserName user={item.to} />} · {item.mode === "race" ? messages.race.title : messages.invites.duel}</span>
                      <span className="mt-1 block text-[0.68rem] leading-relaxed text-muted">
                        {item.mode === "race" ? messages.race.rules : duelRulesSummary(item.rules)}
                      </span>
                    </span>
                    <div className="flex flex-none flex-col items-end gap-1.5">
                      <span className="text-xs font-bold text-muted">
                        {messages.invites.remaining(Math.max(0, Math.ceil((item.expiresAt - now) / 1000)))}
                      </span>
                      <button
                        type="button"
                        className="chip-btn chip-btn--flame"
                        disabled={challengeCancellingId !== null}
                        onClick={() => cancelSentChallenge(item.challengeId, item.to.id)}
                      >
                        {challengeCancellingId === item.challengeId
                          ? messages.friendsScreen.cancellingChallenge
                          : messages.friendsScreen.cancelChallenge}
                      </button>
                    </div>
                  </li>
                ))}
                {data.sentTeamInvites.map((item) => (
                  <li key={item.inviteId} className="row-card">
                    <span className="min-w-0 flex-1">
                      <span className="row-card__name">{<UserName user={item.to} />} · {messages.invites.team}</span>
                      <span className="mt-1 block text-[0.68rem] leading-relaxed text-muted">
                        {messages.invites.teamSummary(item.code, item.settings.durationMinutes, item.playerCount)}
                      </span>
                    </span>
                    <span className="text-xs font-bold text-muted">
                      {messages.invites.remaining(Math.max(0, Math.ceil((item.expiresAt - now) / 1000)))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.incoming.length > 0 && (
            <section>
              <h3 className="section-title">{messages.friendsScreen.friendRequests}</h3>
              <ul className="flex flex-col gap-2">
                {data.incoming.map((item) => (
                  <li key={item.friendshipId} className="row-card">
                    <span className="row-card__name">{<UserName user={item.user} />}</span>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        className="chip-btn"
                        disabled={busy}
                        onClick={() => respond(item.friendshipId, false)}
                      >
                        {messages.rematch.decline}
                      </button>
                      <button
                        type="button"
                        className="chip-btn chip-btn--primary"
                        disabled={busy}
                        onClick={() => respond(item.friendshipId, true)}
                      >
                        {messages.rematch.accept}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h3 className="section-title">{messages.friendsScreen.addFriend}</h3>
            <form onSubmit={search} className="flex gap-2">
              <input
                className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-base"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={messages.friendsScreen.username}
                autoCapitalize="none"
                aria-label={messages.friendsScreen.searchUsername}
              />
              <button type="submit" className="chip-btn chip-btn--primary">
                {messages.friendsScreen.search}
              </button>
            </form>
            <p className="mt-1.5 text-xs text-muted">{messages.friendsScreen.searchHint}</p>

            {results !== null && (
              <ul className="mt-3 flex flex-col gap-2">
                {results.length === 0 && (
                  <li className="text-sm text-muted">{messages.friendsScreen.nobodyFound}</li>
                )}
                {results.map((person) => (
                  <li key={person.id} className="row-card">
                    <span className="row-card__name">{<UserName user={person} />}</span>
                    <button
                      type="button"
                      className="chip-btn chip-btn--primary"
                      disabled={busy}
                      onClick={() => addFriend(person.id)}
                    >
                      {messages.friendsScreen.add}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="section-title">{messages.friendsScreen.yourFriends}</h3>
            {friends.length === 0 ? (
              <p className="text-sm text-muted">
                {messages.friendsScreen.noFriends}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {friends.map((item) => {
                  const left = challengeCooldownLeft(item.user.id, now);
                  return (
                  <li
                    key={item.friendshipId}
                    className="row-card row-card--friend"
                    data-expanded={expandedFriendId === item.friendshipId ? "true" : undefined}
                  >
                    <button
                      type="button"
                      className="row-card__identity"
                      onClick={() => openFriendHistory(item)}
                      aria-label={messages.friendsScreen.openHistory(item.user.username)}
                    >
                      <span className="presence-dot" data-online={item.online ? "true" : undefined} />
                      <span className="row-card__name-text">{<UserName user={item.user} />}</span>
                      <span
                        className="record-badge"
                        title={messages.friendsScreen.record(item.record.wins, item.record.losses, item.record.draws)}
                        aria-label={messages.friendsScreen.record(item.record.wins, item.record.losses, item.record.draws)}
                      >
                        <span className="record-badge__wins">{item.record.wins}{messages.friendHistory.badge.win}</span>
                        <span aria-hidden="true">·</span>
                        <span className="record-badge__losses">{item.record.losses}{messages.friendHistory.badge.loss}</span>
                        <span aria-hidden="true">·</span>
                        <span className="record-badge__draws">{item.record.draws}{messages.friendHistory.badge.draw}</span>
                      </span>
                    </button>
                    <span className="row-card__actions">
                    {/*
                      * Maçtaki arkadaşa meydan okunamıyor (sunucu da reddediyor);
                      * o yüzden aynı yerde "İzle" gösteriliyor.
                      */}
                    {primaryFriendAction(item, left)}
                    <button
                      type="button"
                      className="chip-remove"
                      onClick={() => setRemoving(item)}
                      aria-label={messages.friendsScreen.removeLabel(item.user.username)}
                      title={messages.friendsScreen.remove}
                    >
                      ✕
                    </button>
                    </span>

                    <button
                      type="button"
                      className="friend-card__mobile-summary"
                      aria-expanded={expandedFriendId === item.friendshipId}
                      aria-controls={`friend-options-${item.friendshipId}`}
                      aria-label={
                        expandedFriendId === item.friendshipId
                          ? messages.friendsScreen.hideOptions(item.user.username)
                          : messages.friendsScreen.showOptions(item.user.username)
                      }
                      onClick={() =>
                        setExpandedFriendId((current) =>
                          current === item.friendshipId ? null : item.friendshipId,
                        )
                      }
                    >
                      <span className="presence-dot" data-online={item.online ? "true" : undefined} />
                      <span className="friend-card__mobile-name">{<UserName user={item.user} />}</span>
                      <svg className="friend-card__chevron" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {primaryFriendAction(item, left, true)}

                    <div
                      id={`friend-options-${item.friendshipId}`}
                      className="friend-card__mobile-panel"
                    >
                      {/* Mobil panelde yer var: kısaltma yerine açık yazı. */}
                      <span className="friend-card__mobile-record text-sm text-muted">
                        {messages.friendsScreen.record(item.record.wins, item.record.losses, item.record.draws)}
                      </span>
                      <button
                        type="button"
                        className="chip-btn friend-card__history"
                        onClick={() => openFriendHistory(item)}
                      >
                        {messages.friendsScreen.showHistory}
                      </button>
                      <div className="friend-card__mobile-actions">
                        <button
                          type="button"
                          className="chip-btn friend-card__remove"
                          onClick={() => {
                            setExpandedFriendId(null);
                            setRemoving(item);
                          }}
                        >
                          {messages.friendsScreen.remove}
                        </button>
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </section>

          {data.outgoing.length > 0 && (
            <section>
              <h3 className="section-title">{messages.friendsScreen.sentRequests}</h3>
              <ul className="flex flex-col gap-2">
                {data.outgoing.map((item) => (
                  <li key={item.friendshipId} className="row-card">
                    <span className="row-card__name">{<UserName user={item.user} />}</span>
                    <span className="text-xs text-muted">{messages.friendsScreen.awaitingResponse}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>

      <ConfirmDialog
        open={removing !== null}
        title={messages.friendsScreen.removeTitle}
        message={
          removing
            ? messages.friendsScreen.removeMessage(removing.user.username)
            : ""
        }
        confirmLabel={messages.friendsScreen.removeConfirm}
        cancelLabel={messages.common.cancel}
        busy={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) void removeFriend(removing);
        }}
      />

      <Sheet
        open={configuring !== null}
        title={configuring ? messages.friendsScreen.settingsTitle(configuring.user.username) : messages.friendsScreen.matchSettings}
        accent="flame"
        placement="center"
        onClose={() => {
          if (!challengeSending) setConfiguring(null);
        }}
      >
        {configuring && (
          <div className="duel-settings">
            <fieldset className="duel-settings__group">
              <legend>{messages.friendsScreen.matchSettings}</legend>
              <div className="duel-settings__options duel-settings__options--two">
                {(["classic", "race"] as const).map((mode) => <button key={mode} type="button" className="duel-setting"
                  data-selected={challengeMode === mode || undefined} onClick={() => setChallengeMode(mode)}>
                  {mode === "classic" ? messages.invites.duel : messages.race.title}
                </button>)}
              </div>
            </fieldset>
            {challengeMode === "classic" ? <>
            <fieldset className="duel-settings__group">
              <legend>{messages.friendsScreen.duration}</legend>
              <div className="duel-settings__options">
                {([1, 2, 3] as const).map((durationMinutes) => (
                  <button
                    key={durationMinutes}
                    type="button"
                    className="duel-setting"
                    data-selected={challengeRules.durationMinutes === durationMinutes || undefined}
                    onClick={() => setChallengeRules((rules) => ({ ...rules, durationMinutes }))}
                  >
                    {messages.duel.minutes(durationMinutes)}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="duel-settings__group">
              <legend>{messages.friendsScreen.guessLimit}</legend>
              <div className="duel-settings__options">
                {([4, 5, 6] as const).map((maxAttempts) => (
                  <button
                    key={maxAttempts}
                    type="button"
                    className="duel-setting"
                    data-selected={challengeRules.maxAttempts === maxAttempts || undefined}
                    onClick={() => setChallengeRules((rules) => ({ ...rules, maxAttempts }))}
                  >
                    {messages.duel.attempts(maxAttempts)}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="duel-settings__group">
              <legend>{messages.friendsScreen.victory}</legend>
              <div className="duel-settings__options duel-settings__options--two">
                {(["fewest-attempts", "first-to-solve"] as const).map((victoryRule) => (
                  <button
                    key={victoryRule}
                    type="button"
                    className="duel-setting"
                    data-selected={challengeRules.victoryRule === victoryRule || undefined}
                    onClick={() => setChallengeRules((rules) => ({ ...rules, victoryRule }))}
                  >
                    {victoryRuleLabel(victoryRule)}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset
              className="duel-settings__group"
              disabled={challengeRules.victoryRule === "first-to-solve"}
            >
              <legend>{messages.friendsScreen.pressure}</legend>
              <div className="duel-settings__options duel-settings__options--two">
                {[true, false].map((pressureEnabled) => (
                  <button
                    key={String(pressureEnabled)}
                    type="button"
                    className="duel-setting"
                    data-selected={challengeRules.pressureEnabled === pressureEnabled || undefined}
                    onClick={() => setChallengeRules((rules) => ({ ...rules, pressureEnabled }))}
                  >
                    {pressureEnabled ? messages.friendsScreen.on : messages.friendsScreen.off}
                  </button>
                ))}
              </div>
              {challengeRules.victoryRule === "first-to-solve" && (
                <p className="mt-2 text-xs leading-relaxed text-muted">
                  {messages.friendsScreen.pressureUnused}
                </p>
              )}
            </fieldset>

            <p className="duel-settings__summary">{duelRulesSummary(challengeRules)}</p>

            <button
              type="button"
              className="btn-secondary w-full"
              disabled={challengeSending}
              onClick={() => setChallengeRules(standardDuelRules())}
            >
              {messages.friendsScreen.resetStandard}
            </button>
            </> : <p className="duel-settings__summary">{messages.race.rules}</p>}
            <button
              type="button"
              className="btn-primary w-full"
              disabled={
                challengeSending ||
                challengeCooldownLeft(configuring.user.id, now) > 0 ||
                !(data.friends.find((item) => item.user.id === configuring.user.id)?.online ?? false)
              }
              onClick={challenge}
            >
              {challengeSending
                ? messages.friendsScreen.sending
                : challengeCooldownLeft(configuring.user.id, now) > 0
                  ? messages.friendsScreen.sendAfter(challengeCooldownLeft(configuring.user.id, now))
                  : messages.friendsScreen.sendChallenge}
            </button>
          </div>
        )}
      </Sheet>

      <FriendHistorySheet
        friendshipId={historyFriend?.friendshipId ?? null}
        friend={historyFriend?.user ?? null}
        onClose={() => setHistoryFriend(null)}
        registerBackHandler={registerBackHandler}
      />
    </div>
  );
}
